import { ETextStyleVariant, SkeletonLine, Text } from '@outdoorsyco/bonfire';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { PriceSize } from '@/components/switchback/Price';
import PriceCurrent from '@/components/switchback/Price/PriceCurrent';
import PriceInstantBook from '@/components/switchback/Price/PriceInstantBook';
import { defaultLocale, TCurrency } from '@/config/locales';
import { PriceType } from '@/redux/selectors/listing/page';
import { TEstimatedPriceBreakdown } from '@/services/types/search/rentals/id';

import { PriceDetailsPopover } from './PriceDetailsPopover';

interface TPriceDetailsProps {
  currency?: TCurrency;
  originalPrice: number;
  currentPrice: number;
  instantBook?: boolean;
  period: PriceType;
  size?: PriceSize;
  showDiscount: boolean;
  isBillSection?: boolean;
  priceClassName?: string;
  periodClassName?: string;
  estimatedPriceBreakdown?: TEstimatedPriceBreakdown;
  totalExcludesTaxes?: boolean;
  // Inside a modal the popover must render in place; a body portal sits under the modal overlay.
  renderPopoverInPlace?: boolean;
  isRVBundle?: boolean;
  // MP-5602 — bucketed users see the tax-excluded "$total for N nights" tile.
  displayAsAllInTotal?: boolean;
  hidePeriod?: boolean;
  // MP-7078 — quote is being refreshed for the current URL date window; render
  // a skeleton in place of the numeric price so no stale/base rate flashes.
  isPending?: boolean;
}

export const PriceDetails = ({
  currency = defaultLocale.base_currency,
  originalPrice,
  currentPrice,
  estimatedPriceBreakdown,
  totalExcludesTaxes = false,
  renderPopoverInPlace = false,
  instantBook = false,
  period: pricePeriod,
  size = PriceSize.small,
  showDiscount,
  isBillSection = false,
  priceClassName,
  periodClassName,
  isRVBundle = false,
  displayAsAllInTotal = false,
  hidePeriod,
  isPending = false,
}: TPriceDetailsProps) => {
  const useAllInTotal = displayAsAllInTotal && !!estimatedPriceBreakdown;

  const duration = useAllInTotal ? estimatedPriceBreakdown?.duration : undefined;
  const period = (() => {
    if (estimatedPriceBreakdown) {
      return estimatedPriceBreakdown.use_day_pricing ? PriceType.DAY : PriceType.NIGHT;
    }
    return pricePeriod;
  })();

  const hasDiscount = useAllInTotal
    ? (estimatedPriceBreakdown?.total ?? 0) < (estimatedPriceBreakdown?.original_total ?? 0)
    : currentPrice < originalPrice;

  if (isPending) {
    return (
      <div className="flex flex-col justify-center gap-1">
        <div className={`flex ${priceClassName}`} data-testid="price-details-pending">
          <SkeletonLine width={isBillSection ? '160px' : '120px'} className="bg-gray-200" />
        </div>
      </div>
    );
  }

  const priceCurrent = (
    <PriceCurrent
      size={size}
      currency={currency}
      currentPrice={currentPrice}
      originalPrice={originalPrice}
      period={period}
      isCampsite
      isRVBundle={isRVBundle}
      totalPrice={useAllInTotal ? estimatedPriceBreakdown?.total : undefined}
      duration={duration}
      originalTotalPrice={useAllInTotal ? estimatedPriceBreakdown?.original_total : undefined}
      showDiscount={showDiscount}
      hasDiscount={hasDiscount}
      hidePeriod={hidePeriod}
      periodClassName={periodClassName}
    />
  );

  return (
    <div className="flex flex-col justify-center gap-1">
      <div className={`flex ${priceClassName}`}>
        {instantBook && !isRVBundle && (
          <PriceInstantBook
            className="self-center flex-shrink-0"
            size={isBillSection ? PriceSize.large : PriceSize.medium}
          />
        )}
        {useAllInTotal && estimatedPriceBreakdown && estimatedPriceBreakdown.duration > 0 ? (
          <PriceDetailsPopover
            breakdown={estimatedPriceBreakdown}
            currency={currency}
            totalExcludesTaxes={totalExcludesTaxes}
            usePortal={!renderPopoverInPlace}>
            {priceCurrent}
          </PriceDetailsPopover>
        ) : (
          priceCurrent
        )}
      </div>
      {useAllInTotal && totalExcludesTaxes && (
        <Text variant={ETextStyleVariant.SmallRegular} className="text-gray-500">
          <FormattedMessage
            defaultMessage="before taxes"
            id="n1BlvD"
            description="Search > Listing card > Note under the trip total that taxes are not included"
          />
        </Text>
      )}
    </div>
  );
};
